import type { TerminalSettings } from "./terminal-settings";

/**
 * Соединение веб-клиента с локальным сервером сигналов TigerTrade (§10 1.36,
 * design.md D4, `knowledge/terminals.md`). Чистый объект без React: сокет и
 * таймеры приходят параметрами, чтобы тест подставлял свои.
 *
 * Пока терминал включён, объект держит соединение: пинги, переподключение с
 * растущей паузой, разрыв по тишине. Команда `setLinkSymbol` уходит только в
 * открытое соединение и никогда не копится.
 */

/*
 * Проводка соединения, а не пороги §11: паузы переподключения те же, что у
 * `hooks/use-ipc-connection.ts`; пинг и тишина — по регламенту терминала.
 */
export const RECONNECT_BASE_DELAY_MS = 1000;
export const RECONNECT_MAX_DELAY_MS = 15000;
/** Сколько соединение должно продержаться, чтобы лестница пауз сбросилась (как в `use-ipc-connection.ts`). */
export const STABLE_CONNECTION_MS = 5000;
export const PING_INTERVAL_MS = 10000;
/** Тишина от терминала дольше этого срока — разрыв, любой кадр (включая `pong`) продлевает жизнь. */
export const IDLE_TIMEOUT_MS = 30000;
/** Сколько ждать ответа на команду, прежде чем считать её отправленной без отказа. */
export const ACK_TIMEOUT_MS = 3000;

/** Код закрытия WebSocket «битый JSON»: терминал счёл нашу команду недопустимой. */
const INVALID_PAYLOAD_CLOSE_CODE = 1007;

export type LinkState = "off" | "connecting" | "connected" | "down";

export type LinkOutcome =
  | { kind: "linked" }
  | { kind: "sent" }
  | { kind: "rejected"; message: string }
  /** Кадр в сокет не попал: терминал ничего не получил. */
  | { kind: "not_sent" }
  /** Кадр ушёл, но связь оборвалась до ответа: дошла ли команда, неизвестно. */
  | { kind: "unconfirmed" };

export interface LinkCommand {
  e: string;
  m: string;
  symbol: string;
  linkGroup: string;
}

/** Минимум от сокета, нужный объекту; настоящий `WebSocket` оборачивается `browserSocket`. */
export interface LinkSocket {
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onerror: (() => void) | null;
  send(data: string): void;
  close(): void;
}

/** Таймеры; `Handle` — тип дескриптора, у браузера и у теста он свой. */
export interface TigerLinkTimers<Handle = unknown> {
  setTimeout(run: () => void, ms: number): Handle;
  clearTimeout(handle: Handle): void;
}

export interface TigerLinkOptions<Handle = unknown> {
  /** Может бросить: `new WebSocket` бросает `SyntaxError` на адресе, из которого не собирается URL. */
  createSocket: (url: string) => LinkSocket;
  timers: TigerLinkTimers<Handle>;
}

export interface TigerLink {
  getState: () => LinkState;
  subscribe: (listener: () => void) => () => void;
  configure: (settings: TerminalSettings) => void;
  send: (command: LinkCommand) => Promise<LinkOutcome>;
  dispose: () => void;
}

interface PendingAck<Handle> {
  resolve: (outcome: LinkOutcome) => void;
  timer: Handle;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Ответ терминала на команду; `null` — кадр не ответ (понг, не JSON, чужая форма). */
function parseReply(data: unknown): LinkOutcome | null {
  if (typeof data !== "string") {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.type === "pong") {
    return null;
  }
  const response = parsed.response;
  const responseType = isRecord(response) ? response.type : undefined;
  if (responseType === "pong") {
    return null;
  }
  if (responseType === "successfully") {
    return { kind: "linked" };
  }
  const text = typeof parsed.message === "string" ? parsed.message : parsed.title;
  if (typeof text === "string" && text !== "") {
    return { kind: "rejected", message: text };
  }
  return null;
}

export function createTigerLink<Handle>({
  createSocket,
  timers,
}: TigerLinkOptions<Handle>): TigerLink {
  const listeners = new Set<() => void>();
  let state: LinkState = "off";
  let wanted: { host: string; port: number } | null = null;
  let socket: LinkSocket | null = null;
  let attempt = 0;
  let reconnectTimer: Handle | undefined;
  let stableTimer: Handle | undefined;
  let pingTimer: Handle | undefined;
  let idleTimer: Handle | undefined;
  let pending: PendingAck<Handle> | null = null;
  let disposed = false;

  const moveTo = (next: LinkState) => {
    if (state === next) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener();
    }
  };

  const clearTimer = (handle: Handle | undefined) => {
    if (handle !== undefined) {
      timers.clearTimeout(handle);
    }
  };

  const clearConnectionTimers = () => {
    clearTimer(stableTimer);
    clearTimer(pingTimer);
    clearTimer(idleTimer);
    stableTimer = undefined;
    pingTimer = undefined;
    idleTimer = undefined;
  };

  const settle = (outcome: LinkOutcome) => {
    if (pending === null) {
      return;
    }
    const current = pending;
    pending = null;
    clearTimer(current.timer);
    current.resolve(outcome);
  };

  const armIdle = (owner: LinkSocket) => {
    clearTimer(idleTimer);
    idleTimer = timers.setTimeout(() => lose(owner), IDLE_TIMEOUT_MS);
  };

  const schedulePing = (owner: LinkSocket) => {
    pingTimer = timers.setTimeout(() => {
      if (socket !== owner) {
        return;
      }
      try {
        owner.send("ping");
      } catch {
        lose(owner);
        return;
      }
      schedulePing(owner);
    }, PING_INTERVAL_MS);
  };

  const scheduleReconnect = () => {
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
    attempt += 1;
    reconnectTimer = timers.setTimeout(() => {
      reconnectTimer = undefined;
      connect();
    }, delay);
  };

  /** Соединение потеряно (close, error, тишина): идемпотентно по сокету. */
  const lose = (owner: LinkSocket) => {
    if (socket !== owner) {
      return;
    }
    socket = null;
    clearConnectionTimers();
    owner.onopen = null;
    owner.onmessage = null;
    owner.onclose = null;
    owner.onerror = null;
    try {
      owner.close();
    } catch {
      // Закрывать уже закрытое — не ошибка.
    }
    // Команда в ожидании: неизвестно, дошла ли она. Назвать её «отправленной»
    // нельзя — подтверждения не было, а связь пропала. И `not_sent` тоже:
    // кадр уже ушёл, окно могло открыться.
    settle({ kind: "unconfirmed" });
    if (wanted === null) {
      return;
    }
    moveTo("down");
    scheduleReconnect();
  };

  const connect = () => {
    if (wanted === null || disposed) {
      return;
    }
    moveTo("connecting");
    let owner: LinkSocket;
    try {
      owner = createSocket(`ws://${wanted.host}:${wanted.port}`);
    } catch {
      // Адрес, из которого не собирается URL, не должен ронять экран и не
      // должен залипать в «подключении»: это обычная неудача попытки.
      moveTo("down");
      scheduleReconnect();
      return;
    }
    socket = owner;

    owner.onopen = () => {
      if (socket !== owner) {
        return;
      }
      moveTo("connected");
      stableTimer = timers.setTimeout(() => {
        attempt = 0;
      }, STABLE_CONNECTION_MS);
      schedulePing(owner);
      armIdle(owner);
    };

    owner.onmessage = (event) => {
      if (socket !== owner) {
        return;
      }
      armIdle(owner);
      const reply = parseReply(event.data);
      if (reply !== null) {
        settle(reply);
      }
    };

    owner.onerror = () => lose(owner);

    owner.onclose = (event) => {
      if (socket !== owner) {
        return;
      }
      if (event.code === INVALID_PAYLOAD_CLOSE_CODE) {
        console.error(
          "TigerTrade закрыл соединение кодом 1007: терминал счёл отправленный JSON недопустимым. Это дефект клиента, а не терминала.",
        );
      }
      lose(owner);
    };
  };

  /** Закрывает текущее соединение и ожидание переподключения, не трогая желаемое состояние. */
  const stop = () => {
    clearTimer(reconnectTimer);
    reconnectTimer = undefined;
    clearConnectionTimers();
    const owner = socket;
    socket = null;
    if (owner !== null) {
      owner.onopen = null;
      owner.onmessage = null;
      owner.onclose = null;
      owner.onerror = null;
      try {
        owner.close();
      } catch {
        // Закрывать уже закрытое — не ошибка.
      }
    }
    settle({ kind: "unconfirmed" });
  };

  return {
    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    configure(settings) {
      if (disposed) {
        return;
      }
      if (!settings.enabled) {
        wanted = null;
        stop();
        moveTo("off");
        return;
      }
      if (wanted !== null && wanted.host === settings.host && wanted.port === settings.port) {
        return;
      }
      stop();
      wanted = { host: settings.host, port: settings.port };
      attempt = 0;
      connect();
    },

    send(command) {
      if (state !== "connected" || socket === null) {
        return Promise.resolve({ kind: "not_sent" });
      }
      // Ответы сопоставляются по порядку, одна команда в ожидании за раз:
      // предыдущую закрываем как отправленную, отказа по ней не пришло.
      settle({ kind: "sent" });
      const frame = JSON.stringify({
        type: "setLinkSymbol",
        e: command.e,
        m: command.m,
        symbol: command.symbol,
        linkGroup: command.linkGroup,
      });
      try {
        socket.send(frame);
      } catch {
        return Promise.resolve({ kind: "not_sent" });
      }
      return new Promise<LinkOutcome>((resolve) => {
        const entry: PendingAck<Handle> = {
          resolve,
          timer: timers.setTimeout(() => {
            if (pending === entry) {
              settle({ kind: "sent" });
            }
          }, ACK_TIMEOUT_MS),
        };
        pending = entry;
      });
    },

    dispose() {
      disposed = true;
      wanted = null;
      stop();
      moveTo("off");
    },
  };
}

/** Оборачивает настоящий `WebSocket` в минимальный `LinkSocket`. */
export function browserSocket(url: string): LinkSocket {
  const ws = new WebSocket(url);
  const wrapped: LinkSocket = {
    onopen: null,
    onmessage: null,
    onclose: null,
    onerror: null,
    send: (data) => ws.send(data),
    close: () => ws.close(),
  };
  ws.addEventListener("open", () => wrapped.onopen?.());
  ws.addEventListener("message", (event) => wrapped.onmessage?.({ data: event.data }));
  ws.addEventListener("close", (event) => wrapped.onclose?.({ code: event.code }));
  ws.addEventListener("error", () => wrapped.onerror?.());
  return wrapped;
}

export const browserTimers: TigerLinkTimers<ReturnType<typeof setTimeout>> = {
  setTimeout: (run, ms) => setTimeout(run, ms),
  clearTimeout: (handle) => clearTimeout(handle),
};
